import { Box, Card, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import CusUpload from './CusUpload';
import { useTranslation } from 'react-i18next';
import { IconX } from '@tabler/icons-react';
import ShowContainer from './ShowContainer';
import { isValidFileName } from '../../utils';
import useToast from '../../hooks/useToast';
import { acceptImg } from '../../enums/enums';
import CusTooltip from './CusTooltip';
import InfoDialog from '../shared/InfoDialog';
import { ImageFallback } from './ImageFallback';
import useColor from '../../hooks/site/useColor';

export default function CusUploadImage({
  title,
  id,
  imageSource,
  onChange,
  flexDirection = 'column',
  bannerNearby,
  accept = acceptImg,
  maxSizeMB,
  tooltip,
  onCrossClick,
  Component = Card
}) {
  const { errorToast } = useToast();
  const { t } = useTranslation();
  const { background } = useColor();
  const [image, setImage] = useState(false);
  const [openFullImg, setOpenFullImg] = useState(false);
  const inputRef = useRef();

  const onBaseCrossClick = () => {
    setImage(null);
    inputRef.current.value = '';
    onChange?.('');
  };

  useEffect(() => {
    setImage(imageSource);
  }, [imageSource]);

  const UploadTitle = () => (
    <Stack direction={'row'} gap={0.5}>
      <Typography variant="caption">{t(title)}</Typography>
      <CusTooltip text={tooltip} />
    </Stack>
  );

  const UploadImage = () => (
    <ShowContainer condition={image}>
      <Box position={'relative'} padding={0} width={'fit-content'}>
        <img
          src={image}
          style={{ maxWidth: '200px', maxHeight: '200px', cursor: 'pointer' }}
          onClick={() => setOpenFullImg(true)}
        />

        <IconX
          style={{
            padding: '2px',
            position: 'absolute',
            right: -10,
            top: -10,
            cursor: 'pointer',
            background: `${background.default}`,
            borderRadius: '50%'
          }}
          size={20}
          onClick={onCrossClick || onBaseCrossClick}
        />
      </Box>
    </ShowContainer>
  );

  const UploadUploadFile = () => (
    <CusUpload
      id={id}
      accept={accept}
      maxSizeMB={maxSizeMB}
      ref={inputRef}
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (!isValidFileName(file.name)) {
          errorToast(
            'The file name contains invalid characters. Only letters, numbers, dashes, underscores, and periods are allowed.'
          );
          inputRef.current.value = '';
          return;
        }

        if (file) {
          const reader = new FileReader();

          reader.onloadend = () => {
            const base64String = reader.result || undefined;
            setImage(base64String);
            onChange?.(base64String, file.name);
          };
          reader.readAsDataURL(file);
        }
      }}
    />
  );

  return (
    <>
      <Component sx={{ height: '100%' }}>
        <Stack
          display={'flex'}
          flexDirection={flexDirection}
          alignItems={flexDirection == 'row' ? 'center' : undefined}
          gap={1}
          height={'100%'}>
          {bannerNearby && image ? (
            <Stack direction={'row'} gap={2} alignItems={'center'}>
              <Stack gap={1}>
                <UploadTitle />
                <UploadUploadFile />
              </Stack>
              <Stack>
                <UploadImage />
              </Stack>
            </Stack>
          ) : (
            <>
              <UploadTitle />
              <UploadImage />
              <UploadUploadFile />
            </>
          )}
        </Stack>
      </Component>
      <InfoDialog open={openFullImg} onClose={() => setOpenFullImg(false)}>
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          maxWidth={500}
          maxHeight={500}>
          <ImageFallback src={image} />
        </Box>
      </InfoDialog>{' '}
    </>
  );
}
